import { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ModuleWorkspaceMapPanel } from "@/components/module-map/ModuleWorkspaceMapPanel";
import type { Geometry } from "@/types/api-v2";
import type { UdgspzMapContourItem } from "@/types/udgspz-entity";
import type { DrawToolType } from "@/components/map-libre/MapLibreDrawController";

const DRAW_TOOLS: Array<{ id: DrawToolType; title: string }> = [
  { id: "draw-polygon", title: "Полигон" },
  { id: "draw-rectangle", title: "Прямоугольник" },
  { id: "draw-line", title: "Линия" },
  { id: "draw-point", title: "Точка" },
  { id: "draw-freehand", title: "От руки" },
];

/** Адаптер УДГСПЗ map-contours → контракт thematic-region-overlay. */
function contourFeature(item: UdgspzMapContourItem): GeoJSON.Feature {
  const raw = item.geojson;
  const geometry = raw.type === "Feature" ? raw.geometry : raw;
  return {
    type: "Feature",
    geometry,
    properties: {
      featureId: `udgspz-${item.eudg_map_geom_id}`,
      fillColor: item.fill_color,
      lineColor: item.line_color,
      pattern: item.pattern_code,
      lineWidth: item.line_width ?? 2,
      lineDash: item.line_dash_code ?? "solid",
      label: item.label_text || item.eudg_name,
      labelVisible: item.label_visible !== false,
      labelColor: item.label_color ?? item.line_color,
      labelSize: item.label_size ?? 13,
      textAnchor: item.label_text_anchor ?? "center",
      labelOffsetX: item.label_offset_x ?? 0,
      labelOffsetY: item.label_offset_y ?? 0,
      labelHaloColor: item.label_halo_color ?? "#ffffff",
      labelHaloWidth: item.label_halo_width ?? 2,
      sortKey: item.depth ?? 0,
      labelAnchor: item.label_anchor ?? undefined,
      typeId: item.kls_id_eudg_type,
      eudgId: item.eudg_id,
      parentId: item.eudg_id_parent ?? null,
    },
  };
}

export type UdgspzWorkspaceMapPanelProps = {
  contours: UdgspzMapContourItem[];
  hiddenTypeIds: number[];
  onHiddenTypeChange: (typeId: number, visible: boolean) => void;
  onMvtSelect: (payload: { objectId: number; geometryId?: number }) => void;
  selectedThematicFeatureId?: string | null;
  onThematicFeatureClick?: (featureId: string) => void;
  onClearThematicSelection?: () => void;
  previewGeometry: Geometry | null;
  previewNonce?: number;
  onPreviewConsumed?: () => void;
  collapsed?: boolean;
  onRequestCollapse?: () => void;
  onRequestExpand?: () => void;
  drawingActive: boolean;
  onDrawingActiveChange: (active: boolean) => void;
  canDraw: boolean;
  savePending?: boolean;
  defaultName?: string;
  onSaveDrawn: (payload: { name: string; geojson: GeoJSON.Feature }) => Promise<void>;
};

export function UdgspzWorkspaceMapPanel({
  contours,
  hiddenTypeIds,
  onHiddenTypeChange,
  onMvtSelect,
  selectedThematicFeatureId = null,
  onThematicFeatureClick,
  onClearThematicSelection,
  previewGeometry,
  previewNonce = 0,
  onPreviewConsumed,
  collapsed = false,
  onRequestCollapse,
  onRequestExpand,
  drawingActive,
  onDrawingActiveChange,
  canDraw,
  savePending = false,
  defaultName = "",
  onSaveDrawn,
}: UdgspzWorkspaceMapPanelProps) {
  const [activeTool, setActiveTool] = useState<string | null>("select");
  const [pendingSketch, setPendingSketch] = useState<GeoJSON.Feature | null>(null);
  const [drawName, setDrawName] = useState("");

  useEffect(() => {
    if (drawingActive && canDraw) {
      setActiveTool("draw-polygon");
      setPendingSketch(null);
      setDrawName(defaultName);
      return;
    }
    setActiveTool("select");
    setPendingSketch(null);
    setDrawName("");
    // Имя берётся в момент включения режима, чтобы refetch карточки не сбрасывал черновик.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawingActive, canDraw]);

  const handleDrawingComplete = useCallback((drawing: { geoJSON?: GeoJSON.Feature }) => {
    const feature = drawing?.geoJSON;
    if (!feature?.geometry) return;
    setPendingSketch(feature);
  }, []);

  const handleSave = async () => {
    if (!pendingSketch || !canDraw) return;
    const name = drawName.trim() || defaultName.trim() || "Контур УДГСПЗ";
    await onSaveDrawn({ name, geojson: pendingSketch });
    setPendingSketch(null);
    setDrawName("");
    onDrawingActiveChange(false);
  };

  const hidden = useMemo(() => new Set(hiddenTypeIds), [hiddenTypeIds]);
  const typeOptions = useMemo(() => {
    const byId = new Map<number, string>();
    for (const item of contours) {
      if (!byId.has(item.kls_id_eudg_type)) {
        byId.set(item.kls_id_eudg_type, item.type_name || item.type_code || String(item.kls_id_eudg_type));
      }
    }
    return [...byId.entries()];
  }, [contours]);
  const thematicRegionFeatures = useMemo(
    () => contours.filter((item) => !hidden.has(item.kls_id_eudg_type)).map(contourFeature),
    [contours, hidden],
  );

  const toolbar =
    drawingActive && canDraw ? (
      <>
        <div className="flex shrink-0 flex-wrap items-center gap-1 border-b border-border bg-muted/20 px-2 py-1.5">
          {DRAW_TOOLS.map((tool) => (
            <Button
              key={tool.id}
              type="button"
              size="sm"
              variant={activeTool === tool.id ? "default" : "outline"}
              className="h-7 text-xs"
              onClick={() => {
                setActiveTool(tool.id);
                setPendingSketch(null);
              }}
            >
              {tool.title}
            </Button>
          ))}
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-7 text-xs"
            onClick={() => onDrawingActiveChange(false)}
          >
            Отмена
          </Button>
        </div>
        {pendingSketch ? (
          <div className="flex shrink-0 flex-wrap items-end gap-2 border-b border-border px-2 py-2">
            <div className="min-w-[140px] flex-1 space-y-1">
              <Label htmlFor="udgspz-map-draw-name" className="text-xs">
                Название контура
              </Label>
              <Input
                id="udgspz-map-draw-name"
                className="h-8"
                value={drawName}
                onChange={(e) => setDrawName(e.target.value)}
                placeholder="Название геометрии"
              />
            </div>
            <Button type="button" size="sm" disabled={savePending} onClick={() => void handleSave()}>
              {savePending ? "Сохранение..." : "Сохранить контур"}
            </Button>
          </div>
        ) : null}
      </>
    ) : null;

  const layerBar =
    typeOptions.length > 0 ? (
      <div className="flex shrink-0 flex-wrap items-center gap-3 border-b border-border px-2 py-1.5">
        {typeOptions.map(([typeId, label]) => (
          <label key={typeId} className="flex items-center gap-1.5 text-xs">
            <Checkbox
              checked={!hidden.has(typeId)}
              onCheckedChange={(checked) => onHiddenTypeChange(typeId, checked === true)}
            />
            {label}
          </label>
        ))}
      </div>
    ) : null;

  return (
    <ModuleWorkspaceMapPanel
      title="Карта (УДГСПЗ)"
      collapsed={collapsed}
      onRequestCollapse={onRequestCollapse}
      onRequestExpand={onRequestExpand}
      onMvtSelect={onMvtSelect}
      thematicRegionFeatures={thematicRegionFeatures}
      selectedThematicFeatureId={selectedThematicFeatureId}
      onThematicFeatureClick={onThematicFeatureClick}
      onClearObjectSelection={onClearThematicSelection}
      previewGeometry={previewGeometry}
      previewNonce={previewNonce}
      onPreviewConsumed={onPreviewConsumed}
      toolbar={
        <>
          {layerBar}
          {toolbar}
        </>
      }
      activeTool={drawingActive && canDraw ? activeTool : "select"}
      onActiveToolChange={setActiveTool}
      isDrawing={drawingActive && canDraw}
      pendingSketchFeature={pendingSketch}
      onDrawingComplete={handleDrawingComplete}
    />
  );
}
